"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "@/i18n/routing";
import FallbackImage from "@/components/ui/FallbackImage";
import { X, Plus, Minus, ShoppingBag, Trash2, Check, Heart, Sparkles, Truck, MapPin, AlertCircle, Percent, Gift } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatPrice } from "@/lib/utils";
import { useCart, MAX_QUANTITY_PER_ITEM, GIFT_WRAP_PRICE } from "@/context/CartContext";
import { useTranslations } from "next-intl";
import { FREE_SHIPPING_THRESHOLD } from "@/lib/constants";

/* ─── Types for upsell products ─── */
interface UpsellProduct {
  id: string;
  name: string;
  slug: string;
  price: number;
  image?: string;
  categoryId: string;
}

/* ─── Animated number counter ─── */
function AnimatedPrice({ value }: { value: number }) {
  const [displayed, setDisplayed] = useState(value);
  const [animating, setAnimating] = useState(false);
  const prevRef = useRef(value);

  useEffect(() => {
    if (prevRef.current === value) return;
    const from = prevRef.current;
    const to = value;
    prevRef.current = value;
    setAnimating(true);

    const duration = 400;
    const start = performance.now();

    function tick(now: number) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayed(from + (to - from) * eased);
      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        setDisplayed(to);
        setAnimating(false);
      }
    }

    requestAnimationFrame(tick);
  }, [value]);

  return (
    <span className={cn("transition-transform duration-200", animating && "animate-price-pulse")}>
      {formatPrice(displayed)}
    </span>
  );
}

/* ─── Cart item with animation ─── */
function CartItem({
  item,
  onRemove,
  onUpdateQuantity,
  onSetGiftWrap,
  onSetGiftMessage,
  removeLabel,
  decreaseLabel,
  increaseLabel,
  maxQtyReachedId,
  maxQtyMessage,
  addGiftWrapLabel,
  giftMessagePlaceholder,
}: {
  item: {
    id: string;
    productId?: string;
    name: string;
    price: number;
    quantity: number;
    image?: string;
    customConfig?: Record<string, string>;
    giftWrap?: boolean;
    giftMessage?: string;
  };
  onRemove: (id: string) => void;
  onUpdateQuantity: (id: string, qty: number) => void;
  onSetGiftWrap: (id: string, giftWrap: boolean) => void;
  onSetGiftMessage: (id: string, message: string) => void;
  removeLabel: string;
  decreaseLabel: string;
  increaseLabel: string;
  maxQtyReachedId: string | null;
  maxQtyMessage: string;
  addGiftWrapLabel: string;
  giftMessagePlaceholder: string;
}) {
  const [removing, setRemoving] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const prevQtyRef = useRef(item.quantity);

  useEffect(() => {
    if (item.quantity > prevQtyRef.current) {
      setJustAdded(true);
      const timer = setTimeout(() => setJustAdded(false), 300);
      return () => clearTimeout(timer);
    }
    prevQtyRef.current = item.quantity;
  }, [item.quantity]);

  const handleRemove = useCallback(() => {
    setRemoving(true);
    // Wait for exit animation before actually removing
    setTimeout(() => onRemove(item.id), 280);
  }, [item.id, onRemove]);

  const isAtMax = item.quantity >= MAX_QUANTITY_PER_ITEM;
  const showMaxMessage = maxQtyReachedId === item.id;

  return (
    <div
      className={cn(
        "flex gap-4 py-4 border-b border-gray-50 last:border-0 transition-all duration-300",
        removing && "opacity-0 translate-x-8 max-h-0 py-0 overflow-hidden",
        !removing && "animate-fade-up-fill opacity-100 max-h-48",
        justAdded && "bg-brand-cream/30"
      )}
    >
      {/* Image */}
      <div className={cn(
        "relative h-24 w-20 flex-shrink-0 bg-brand-cream overflow-hidden transition-transform duration-300",
        justAdded && "scale-105"
      )}>
        {item.image ? (
          <FallbackImage
            src={item.image}
            alt={item.name}
            fill
            className="object-cover"
            sizes="80px"
            fallbackClassName="h-full w-full flex items-center justify-center bg-brand-cream"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <ShoppingBag className="h-6 w-6 text-brand-gold/30" />
          </div>
        )}
        {justAdded && (
          <div className="absolute inset-0 flex items-center justify-center bg-brand-gold/20 animate-fade-in">
            <Check className="h-5 w-5 text-brand-gold" />
          </div>
        )}
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-medium text-brand-dark truncate">
          {item.name}
        </h3>
        <p className="text-sm text-brand-gold mt-1">
          {formatPrice(item.price)}
        </p>

        {/* Custom config details */}
        {item.customConfig && (
          <div className="mt-1 flex flex-wrap gap-1">
            {Object.entries(item.customConfig).map(([key, value]) => (
              <span
                key={key}
                className="text-[11px] text-brand-muted bg-brand-cream px-1.5 py-0.5"
              >
                {value}
              </span>
            ))}
          </div>
        )}

        {/* Quantity controls */}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center border border-gray-200 rounded-sm overflow-hidden">
            <button
              onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-brand-dark/50 hover:text-brand-dark hover:bg-gray-50 transition-colors"
              aria-label={decreaseLabel}
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <span
              key={item.quantity}
              className="w-8 text-center text-xs font-medium text-brand-dark animate-scale-in"
            >
              {item.quantity}
            </span>
            <button
              onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
              disabled={isAtMax}
              className={cn(
                "min-h-[44px] min-w-[44px] flex items-center justify-center transition-colors",
                isAtMax
                  ? "text-brand-dark/20 cursor-not-allowed"
                  : "text-brand-dark/50 hover:text-brand-dark hover:bg-gray-50"
              )}
              aria-label={increaseLabel}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>

          <button
            onClick={handleRemove}
            className="p-1.5 text-brand-dark/30 hover:text-red-500 transition-colors group"
            aria-label={removeLabel}
          >
            <Trash2 className="h-4 w-4 transition-transform group-hover:scale-110" />
          </button>
        </div>

        {/* Max quantity warning */}
        {showMaxMessage && (
          <div className="flex items-center gap-1.5 mt-2 text-[11px] text-amber-600 animate-fade-in">
            <AlertCircle className="h-3 w-3 flex-shrink-0" />
            <span>{maxQtyMessage}</span>
          </div>
        )}

        {/* Gift wrap toggle */}
        <div className="mt-2.5">
          <label className="flex items-center gap-2 text-[11px] text-brand-muted cursor-pointer hover:text-brand-dark transition-colors">
            <input
              type="checkbox"
              checked={!!item.giftWrap}
              onChange={(e) => onSetGiftWrap(item.id, e.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300 text-brand-gold focus:ring-brand-gold/40"
            />
            <Gift className="h-3 w-3 text-brand-gold" />
            <span>{addGiftWrapLabel}</span>
          </label>
          {item.giftWrap && (
            <textarea
              value={item.giftMessage || ""}
              onChange={(e) => onSetGiftMessage(item.id, e.target.value.slice(0, 500))}
              placeholder={giftMessagePlaceholder}
              rows={2}
              className="mt-2 w-full text-[11px] rounded-sm border border-gray-200 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold resize-none"
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Upsell product card (drawer) ─── */
function UpsellCard({
  product,
  onAdd,
  addLabel,
}: {
  product: UpsellProduct;
  onAdd: () => void;
  addLabel: string;
}) {
  return (
    <div className="flex-shrink-0 w-32">
      <div className="relative h-36 w-full bg-brand-cream overflow-hidden mb-2">
        {product.image ? (
          <FallbackImage
            src={product.image}
            alt={product.name}
            fill
            className="object-cover"
            sizes="128px"
            fallbackClassName="h-full w-full flex items-center justify-center bg-brand-cream"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <ShoppingBag className="h-6 w-6 text-brand-gold/30" />
          </div>
        )}
      </div>
      <h4 className="text-xs font-medium text-brand-dark truncate">{product.name}</h4>
      <p className="text-xs text-brand-gold mt-0.5">{formatPrice(Number(product.price))}</p>
      <button
        onClick={onAdd}
        className="mt-2 w-full text-[11px] uppercase tracking-wider font-medium text-brand-gold border border-brand-gold/30 py-1.5 hover:bg-brand-gold hover:text-white transition-colors"
      >
        {addLabel}
      </button>
    </div>
  );
}

/* ─── Delivery estimate banner ─── */
function DeliveryEstimate({ subtotal, t }: { subtotal: number; t: ReturnType<typeof useTranslations> }) {
  const hasFreeShipping = subtotal >= FREE_SHIPPING_THRESHOLD;

  return (
    <div className="bg-brand-cream/50 px-4 py-3 space-y-2">
      {/* Free shipping info */}
      <div className="flex items-start gap-2">
        <Truck className="h-3.5 w-3.5 text-brand-gold mt-0.5 flex-shrink-0" />
        <span className="text-[11px] text-brand-dark">
          {hasFreeShipping
            ? t("freeShippingQualified")
            : t("freeShippingThreshold", { amount: formatPrice(FREE_SHIPPING_THRESHOLD) })}
        </span>
      </div>
      {/* Local delivery info */}
      <div className="flex items-start gap-2">
        <MapPin className="h-3.5 w-3.5 text-brand-gold mt-0.5 flex-shrink-0" />
        <span className="text-[11px] text-brand-muted">
          {t("localDeliveryAvailable")}
        </span>
      </div>
    </div>
  );
}

/* ─── Main CartDrawer ─── */
export default function CartDrawer() {
  const {
    items,
    isOpen,
    closeCart,
    removeItem,
    updateQuantity,
    setGiftWrap,
    setGiftMessage,
    subtotal,
    giftWrapTotal,
    itemCount,
    addItem,
    maxQuantityReached,
  } = useCart();
  const t = useTranslations("cart");
  const tc = useTranslations("common");
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const itemListRef = useRef<HTMLDivElement>(null);
  const lastItemCountRef = useRef(items.length);

  // Scroll the item list to the most-recently-added row whenever the
  // cart count grows. Without this the new item lands at the bottom
  // below the visible area on a long cart, making "Add to cart" feel
  // silent.
  useEffect(() => {
    if (!isOpen) {
      lastItemCountRef.current = items.length;
      return;
    }
    if (items.length > lastItemCountRef.current && itemListRef.current) {
      requestAnimationFrame(() => {
        const list = itemListRef.current;
        if (!list) return;
        // Scroll to the bottom of the list — newest item is appended.
        list.scrollTo({ top: list.scrollHeight, behavior: "smooth" });
      });
    }
    lastItemCountRef.current = items.length;
  }, [items.length, isOpen]);

  // Focus trap and Escape key for cart drawer
  useEffect(() => {
    if (!isOpen) return;

    // Focus the close button when drawer opens
    setTimeout(() => closeButtonRef.current?.focus(), 100);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeCart();
        return;
      }

      if (e.key !== "Tab" || !drawerRef.current) return;

      const focusableSelectors =
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const focusableElements = Array.from(
        drawerRef.current.querySelectorAll<HTMLElement>(focusableSelectors)
      );

      if (focusableElements.length === 0) return;

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    document.body.style.paddingInlineEnd = `${scrollbarWidth}px`;

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
      document.body.style.paddingInlineEnd = "";
    };
  }, [isOpen, closeCart]);

  // Upsell products state
  const [upsellProducts, setUpsellProducts] = useState<UpsellProduct[]>([]);

  // Fetch "frequently bought together" suggestions when cart contents change.
  // Uses real co-purchase data from past orders, falls back to category-based
  // recommendations (preferring accessories) when there's not enough order history.
  useEffect(() => {
    if (items.length === 0) {
      setUpsellProducts([]);
      return;
    }

    const cartProductIds = items.map((item) => item.productId).filter(Boolean) as string[];
    if (cartProductIds.length === 0) {
      setUpsellProducts([]);
      return;
    }

    fetch(`/api/recommendations/cart?productIds=${cartProductIds.join(",")}&limit=3`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data.recommendations)) {
          const mapped: UpsellProduct[] = data.recommendations.map(
            (p: {
              id: string;
              name: string;
              slug: string;
              price: number;
              images?: { url: string }[];
              category?: { slug: string };
            }) => ({
              id: p.id,
              name: p.name,
              slug: p.slug,
              price: Number(p.price),
              image: p.images?.[0]?.url,
              categoryId: p.category?.slug || "",
            })
          );
          setUpsellProducts(mapped);
        }
      })
      .catch((err) => {
        console.warn("Upsell fetch failed (non-critical):", err);
      });
  }, [items]);

  // ─── Automatic discount rules preview ───
  // Calls the server-side rules engine to show what (if any) automatic
  // discounts will fire at checkout. Pure preview — never persists.
  const [appliedRules, setAppliedRules] = useState<
    { ruleId: string; name: string; amount: number }[]
  >([]);
  const [rulesDiscount, setRulesDiscount] = useState(0);

  useEffect(() => {
    if (items.length === 0) {
      setAppliedRules([]);
      setRulesDiscount(0);
      return;
    }
    const controller = new AbortController();
    const payload = {
      items: items.map((i) => ({
        productId: i.productId,
        customPrice: i.productId ? undefined : i.price,
        quantity: i.quantity,
      })),
    };
    fetch("/api/discount-rules/preview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: controller.signal,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        setAppliedRules(data.appliedRules || []);
        setRulesDiscount(Number(data.totalDiscount) || 0);
      })
      .catch((err) => {
        console.warn("Discount preview failed (non-critical):", err);
      });
    return () => controller.abort();
  }, [items]);

  // Track previous itemCount to detect add/remove events for aria-live announcement
  const prevItemCountRef = useRef(itemCount);
  const [liveMessage, setLiveMessage] = useState("");

  useEffect(() => {
    const prev = prevItemCountRef.current;
    if (itemCount > prev) {
      setLiveMessage(t("liveItemAdded", { count: itemCount, total: formatPrice(subtotal) }));
    } else if (itemCount < prev && itemCount >= 0) {
      setLiveMessage(t("liveItemRemoved", { count: itemCount, total: formatPrice(subtotal) }));
    }
    prevItemCountRef.current = itemCount;
  }, [itemCount, subtotal, t]);

  const handleAddUpsell = useCallback(
    (product: UpsellProduct) => {
      addItem({
        productId: product.id,
        categoryId: product.categoryId,
        name: product.name,
        price: product.price,
        quantity: 1,
        image: product.image,
      });
    },
    [addItem]
  );

  return (
    <>
      {/* Visually hidden aria-live region for cart updates */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {liveMessage}
      </div>

      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity duration-300",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={closeCart}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("yourCart")}
        className={cn(
          "fixed top-0 end-0 z-50 h-full w-full max-w-md bg-white",
          "transform transition-transform duration-300 ease-out",
          "flex flex-col shadow-2xl",
          isOpen ? "translate-x-0" : "ltr:translate-x-full rtl:-translate-x-full"
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <ShoppingBag className="h-5 w-5 text-brand-dark" />
            <div>
              <h2 className="font-display text-lg tracking-wider text-brand-dark">
                {t("yourCart")}
              </h2>
              {itemCount > 0 && (
                <span
                  key={itemCount}
                  className="text-[11px] text-brand-muted animate-fade-in"
                >
                  {t("itemCountBag", { count: itemCount })}
                </span>
              )}
            </div>
          </div>
          <button
            ref={closeButtonRef}
            onClick={closeCart}
            className="p-2 -me-2 text-brand-dark/50 hover:text-brand-dark transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50 rounded"
            aria-label={tc("close")}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Cart content */}
        {items.length === 0 ? (
          /* ─── Empty state ─── */
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center animate-fade-in">
            {/* Decorative illustration */}
            <div className="relative mb-8">
              <div className="flex items-center justify-center w-24 h-24 rounded-full bg-brand-cream">
                <ShoppingBag className="h-10 w-10 text-brand-gold/40" />
              </div>
              <div className="absolute -top-1 -end-1">
                <Sparkles className="h-5 w-5 text-brand-gold/60" />
              </div>
              <div className="absolute -bottom-1 -start-2">
                <Heart className="h-4 w-4 text-brand-gold/40" />
              </div>
            </div>
            <p className="font-display text-xl text-brand-dark mb-2">
              {t("empty")}
            </p>
            <p className="text-sm text-brand-muted mb-2 max-w-xs leading-relaxed">
              {t("emptyDesc")}
            </p>
            <p className="text-xs text-brand-muted mb-8">
              {t("emptyHint")}
            </p>
            <Link
              href="/wigs"
              onClick={closeCart}
              className="btn-primary"
            >
              {t("continueShopping")}
            </Link>
          </div>
        ) : (
          <>
            {/* Delivery estimate banner */}
            <DeliveryEstimate subtotal={subtotal} t={t} />

            {/* Items list */}
            <div ref={itemListRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-0">
              {items.map((item) => (
                <CartItem
                  key={item.id}
                  item={item}
                  onRemove={removeItem}
                  onUpdateQuantity={updateQuantity}
                  onSetGiftWrap={setGiftWrap}
                  onSetGiftMessage={setGiftMessage}
                  removeLabel={t("remove")}
                  decreaseLabel={tc("decreaseQuantity")}
                  increaseLabel={tc("increaseQuantity")}
                  maxQtyReachedId={maxQuantityReached}
                  maxQtyMessage={t("maxQuantityReached", { max: MAX_QUANTITY_PER_ITEM })}
                  addGiftWrapLabel={t("addGiftWrap", { amount: formatPrice(GIFT_WRAP_PRICE) })}
                  giftMessagePlaceholder={t("giftMessagePlaceholder")}
                />
              ))}

              {/* ─── Upsell section ─── */}
              {upsellProducts.length > 0 && (
                <div className="pt-6 mt-4 border-t border-gray-100">
                  <h3 className="text-xs font-medium uppercase tracking-widest text-brand-muted mb-4">
                    {t("youMightAlsoLike")}
                  </h3>
                  <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
                    {upsellProducts.map((product) => (
                      <UpsellCard
                        key={product.id}
                        product={product}
                        onAdd={() => handleAddUpsell(product)}
                        addLabel={tc("quickAdd")}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Bottom section */}
            <div className="border-t border-gray-100 px-6 py-6 space-y-4">
              {/* Subtotal breakdown */}
              <div className="space-y-2">
                {/* Item count */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-brand-muted">
                    {t("itemCount", { count: itemCount })}
                  </span>
                  <span className="text-xs text-brand-muted">
                    <AnimatedPrice value={subtotal} />
                  </span>
                </div>

                {/* Estimated shipping */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-brand-muted">
                    {t("estimatedShipping")}
                  </span>
                  <span className="text-xs text-brand-muted">
                    {subtotal >= FREE_SHIPPING_THRESHOLD
                      ? t("free")
                      : t("shippingCalculated")}
                  </span>
                </div>

                {/* Gift wrap line */}
                {giftWrapTotal > 0 && (
                  <div className="flex items-center justify-between animate-fade-in">
                    <span className="flex items-center gap-1.5 text-xs text-brand-gold">
                      <Gift className="h-3 w-3 flex-shrink-0" />
                      {t("giftWrap")}
                    </span>
                    <span className="text-xs text-brand-gold font-medium">
                      +{formatPrice(giftWrapTotal)}
                    </span>
                  </div>
                )}

                {/* Automatic discount rules — preview */}
                {appliedRules.map((rule) => (
                  <div
                    key={rule.ruleId}
                    className="flex items-center justify-between animate-fade-in"
                  >
                    <span className="flex items-center gap-1.5 text-xs text-emerald-600">
                      <Percent className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{rule.name}</span>
                    </span>
                    <span className="text-xs text-emerald-600 font-medium">
                      -{formatPrice(rule.amount)}
                    </span>
                  </div>
                ))}

                {/* Divider */}
                <div className="border-t border-gray-100 pt-2" />

                {/* Subtotal total */}
                <div className="flex items-center justify-between" aria-live="polite" aria-atomic="true">
                  <span className="text-sm font-medium uppercase tracking-wider text-brand-dark">
                    {t("subtotal")}
                  </span>
                  <span className="text-lg font-display text-brand-dark">
                    <AnimatedPrice value={Math.max(0, subtotal - rulesDiscount + giftWrapTotal)} />
                  </span>
                </div>
              </div>

              {/* Free shipping progress */}
              {subtotal < FREE_SHIPPING_THRESHOLD && subtotal > 0 && (
                <div className="animate-fade-in">
                  <div className="flex items-center gap-1.5 text-[11px] text-brand-muted mb-1">
                    <Truck className="h-3 w-3 text-brand-gold flex-shrink-0" />
                    <span>{t("addMoreFreeShipping", { amount: formatPrice(FREE_SHIPPING_THRESHOLD - subtotal) })}</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-gold rounded-full transition-all duration-700 ease-out"
                      style={{ width: `${Math.min((subtotal / FREE_SHIPPING_THRESHOLD) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="space-y-3">
                <Link
                  href="/checkout"
                  onClick={closeCart}
                  className="btn-primary w-full text-center"
                >
                  {t("checkout")}
                </Link>
                <Link
                  href="/cart"
                  onClick={closeCart}
                  className="btn-secondary w-full text-center"
                >
                  {t("viewCart")}
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
